// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import translate from "@insite/client-framework/Translate";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import DatePicker, {
    DatePickerPresentationProps,
    DatePickerState,
} from "@store/Library/Components/Reporting/OriginalDatePicker/DatePicker";
import * as React from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

import getColor from "@insite/mobius/utilities/getColor";

import {
    getOneMonthAgoDateStringWithoutTime,
    getTodayDateStringWithoutTime,
} from "@store/Framework/Common/Utilities/reportingUtils";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useUpdateReportSearchFields from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useUpdateSearchFields";

export interface ReportDateRangeStyles {
    searchFieldWrapper?: SearchFieldWrapperStyles;
    datePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
}

export const dateRangeStyles: ReportDateRangeStyles = {
    datePickersWrapper: {
        css: css`
            align-items: flex-end;
            display: flex;
            flex-wrap: wrap;
            column-gap: 10px;
            row-gap: 1.5rem;
            width: 100%;
        `,
    },
    fromDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            datePicker: css`
                width: 100%;
            `,
        },
    },
    toDate: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            // formField: css`
            //     padding-left: 5px;
            // `,
        },
        labelProps: {
            css: css`
                @media (min-width: 768px) {
                    color: ${getColor("common.background")};
                }
            `,
        },
    },
};
interface FromToDate {
    startDate: Date;
    endDate: Date;
}

const styles = dateRangeStyles;
const ReportSearchFieldDateRange: React.FunctionComponent = () => {
    const parameters = useSelector(reportParametersSelector);
    const [dateRange, setDateRange] = React.useState<FromToDate>({
        startDate: new Date(2021, 1, 1),
        endDate: new Date(2023, 1, 1),
    });
    const updateReportSearchFields = useUpdateReportSearchFields();

    const dateChangeHandler = (
        { selectedDay }: Pick<DatePickerState, "selectedDay">,
        dateString: "startDate" | "endDate",
    ) => {
        const loadParameter = {
            ...parameters,
            [dateString]: convertDateToDateOnlyString(selectedDay),
        };
        setDateRange({ ...dateRange, [dateString]: selectedDay });
        updateReportSearchFields(loadParameter);
    };
    // bug in the convert back to date string function. adds a day each time.
    const startDate = new Date(parameters?.startDate);
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(parameters?.endDate);
    endDate.setDate(endDate.getDate() + 1);

    // awkward work around because of how react-date-picker works when controlled and styling issues
    if (startDate.toString() === "Invalid Date" || endDate.toString() === "Invalid Date") {
        updateReportSearchFields({
            ...parameters,
            startDate: getOneMonthAgoDateStringWithoutTime(),
            endDate: getTodayDateStringWithoutTime(),
        });
        return null;
    }

    return (
        <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
            <StyledWrapper {...styles.datePickersWrapper} data-test-selector="report_filterDateRange">
                <DatePicker
                    data-test-selector="report_filterFromDate"
                    {...styles.fromDate}
                    label={translate("Date Range")}
                    selectedDay={startDate}
                    onDayChange={data => dateChangeHandler(data, "startDate")}
                    dateTimePickerProps={{
                        clearIcon: null,
                        maxDate: endDate,
                        ...styles.fromDate?.dateTimePickerProps,
                    }}
                />
                <DatePicker
                    data-test-selector="report_filterToDate"
                    {...styles.toDate}
                    label={translate("To")}
                    selectedDay={endDate}
                    onDayChange={data => dateChangeHandler(data, "endDate")}
                    dateTimePickerProps={{
                        clearIcon: null,
                        minDate: startDate,
                        ...styles.toDate?.dateTimePickerProps,
                    }}
                />
            </StyledWrapper>
        </SearchFieldWrapper>
    );
};

const widgetModule: CustomWidgetModule = {
    component: ReportSearchFieldDateRange,
    definition: {
        group: "Reporting",
        displayName: "Date Range",
    },
};

export default widgetModule;
